import { useCallback, useEffect, useRef, useState } from "react";
import { mergeBuildProgress } from "../../domain/build/buildProgress";
import { normalizeFinishes } from "../../domain/finishes/types";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import { DEFAULT_SITE_CONDITIONS } from "../../domain/foundation/siteConditions";
import { normalizeWallHeightM } from "../../domain/geometry/buildingDimensions";
import { normalizeStoredIndoorRoom } from "../../domain/indoor/indoorRoom";
import type { ClimateProfile, Market } from "../../domain/location/types";
import { snapRotationDeg } from "../../domain/viewer/saunaRotation";
import { resolveTwoRoomConfig } from "../../engines/twoRoomSauna";
import type {
	RoofConfig,
	SaunaDimensions,
	ThermalInputs,
	TwoRoomConfig,
} from "../../engines/types";
import { getT } from "../../i18n";
import type { ShareableConfig } from "../../lib/shareConfig";
import {
	buildProgressLocalKey,
	clearLocalBuildProgress,
	readLocalBuildProgress,
	writeLocalBuildProgress,
} from "../../store/buildProgressLocalStore";
import type { BuilderStore } from "../../store/useBuilderStore";
import {
	loadDesign,
	startAutoSave,
	useBuilderStore,
	useTemporalStore,
} from "../../store/useBuilderStore";
import type { SaunaType } from "../../types/design";
import {
	clearLocalDraft,
	draftLocalKey,
	readLocalDraft,
	writeLocalDraft,
} from "../../store/draftLocalStore";
import { DEFAULT_THERMAL, DEFAULT_VIS } from "./constants";
import { buildShareableConfig } from "./draftConfig";
import { getDefaultOpenings } from "./defaultOpenings";

interface BuilderDesignLifecycleOptions {
	designId: string | null;
	type: SaunaType;
	initialSeedKey: string;
	initialDimensions: SaunaDimensions;
	initialPartitionSplit?: number;
	initialTwoRoomConfig: Partial<TwoRoomConfig>;
	initialRoofConfig: RoofConfig;
	initialThermalInputs: Partial<ThermalInputs>;
	initialSiteConditions: Partial<SiteConditions>;
	initialClimateProfile: ClimateProfile;
	market: Market;
	wizardZip: string;
	wizardClimate: string;
	/** The wizard's "2/4/6 personer" size tier, for a FRESH seed draft only. */
	initialWizardCapacityHint: number | null;
	mergeSearchParams: (
		nextInit: URLSearchParams | Record<string, string>,
		opts?: { replace?: boolean },
	) => void;
	/** Replace-navigate to another type's canonical builder URL (search preserved). */
	navigateToType: (nextType: SaunaType) => void;
	resetToDefaults: BuilderStore["resetToDefaults"];
	userId: string | null;
	/** Decoded share config from ?share= param — enables lossless roundtrip. */
	shareConfig: ShareableConfig | null;
}

// Restore device-local build progress into a freshly-seeded anonymous draft.
// resetToDefaults has already set buildProgress to {}, so this runs after it and
// only adds stored completions — never a save-triggering fingerprint change for a
// signed-in design (which never reaches this path). Module-level so it is not a
// hook dependency.
function restoreLocalBuildProgress(
	draftType: SaunaType,
	seedKey: string,
): void {
	const stored = readLocalBuildProgress(
		buildProgressLocalKey(draftType, seedKey),
	);
	if (Object.keys(stored).length === 0) return;
	useBuilderStore.setState({ buildProgress: stored });
}

/** Settle time before a draft change is written to device storage. */
const DRAFT_WRITE_DELAY_MS = 400;

/**
 * Store state for a design snapshot (a share link or a device-local draft).
 * The hydration REPLACES the store: every field is written even when absent,
 * so nothing from the previously-open design leaks into this one.
 */
function configToStoreState(
	shareConfig: ShareableConfig,
	resolvedMarket: Market,
	resolvedType: SaunaType,
	initialClimateProfile: ClimateProfile,
) {
	return {
		market: resolvedMarket,
		locationMode:
			shareConfig.locationMode ??
			(resolvedMarket === "intl" ? "manual_climate" : "postal_code"),
		climateProfile: shareConfig.climateProfile ?? initialClimateProfile,
		dimensions: { width: shareConfig.width, length: shareConfig.length },
		partitionSplit: shareConfig.partitionSplit ?? 0.5,
		twoRoomConfig: resolveTwoRoomConfig(shareConfig),
		timeOfDay: shareConfig.timeOfDay ?? 0,
		rotationDeg: snapRotationDeg(shareConfig.rotationDeg),
		visibility: DEFAULT_VIS,
		openings: shareConfig.openings?.length
			? shareConfig.openings
			: getDefaultOpenings(
					resolvedType,
					{ width: shareConfig.width, length: shareConfig.length },
					{
						partitionSplit: shareConfig.partitionSplit ?? 0.5,
						twoRoomConfig: shareConfig,
					},
				),
		deckConfig: shareConfig.deckConfig ?? null,
		// Must be written even when absent: this hydration REPLACES the store,
		// so leaving it out kept the previously-open design's benches and then
		// let autosave persist them onto the shared design.
		interiorLayout: shareConfig.interiorLayout ?? null,
		// Written unconditionally for the same reason: this hydration
		// REPLACES the store, so an absent value must land as the
		// defaults rather than inheriting the previous design's colour.
		finishes: normalizeFinishes(shareConfig.finishes),
		// Same: an older link opens at the default height, not the
		// previous design's.
		wallHeightM: normalizeWallHeightM(shareConfig.wallHeightM),
		// Same: an older link opens free-standing.
		indoorRoom:
			resolvedType === "inomhus"
				? normalizeStoredIndoorRoom(shareConfig.indoorRoom)
				: null,
		wizardCapacityHint: shareConfig.capacityHint ?? null,
		roofConfig: shareConfig.roofConfig,
		buildProgress: {},
		thermalInputs: {
			...DEFAULT_THERMAL,
			heaterType: shareConfig.heaterType,
			heaterModelId: shareConfig.heaterModelId ?? null,
			glassAreaSqm: shareConfig.glassAreaSqm,
			stoneAreaSqm: shareConfig.stoneAreaSqm,
			isOutdoorLogCabin: shareConfig.isOutdoorLogCabin,
			includesShower: shareConfig.includesShower,
			saunaLighting: shareConfig.saunaLighting,
			heaterPlacementZMm: shareConfig.heaterPlacementZMm,
		},
		siteConditions: {
			...DEFAULT_SITE_CONDITIONS,
			...shareConfig.siteConditions,
		},
		projectName:
			shareConfig.projectName ??
			getT(resolvedMarket).rail_defaultProjectName,
		postalCode: shareConfig.postalCode ?? "",
	};
}

export function useBuilderDesignLifecycle({
	designId,
	type,
	initialSeedKey,
	initialDimensions,
	initialPartitionSplit,
	initialTwoRoomConfig,
	initialRoofConfig,
	initialThermalInputs,
	initialSiteConditions,
	initialClimateProfile,
	market,
	wizardZip,
	wizardClimate,
	initialWizardCapacityHint,
	mergeSearchParams,
	navigateToType,
	resetToDefaults,
	userId,
	shareConfig,
}: BuilderDesignLifecycleOptions) {
	const storeRef = useRef(useBuilderStore.getState());
	const mergeSearchParamsRef = useRef(mergeSearchParams);
	mergeSearchParamsRef.current = mergeSearchParams;
	const navigateToTypeRef = useRef(navigateToType);
	navigateToTypeRef.current = navigateToType;
	const lastInitializationKeyRef = useRef<string | null>(null);
	const pendingDesignLoadRef = useRef(false);
	const [persistenceReady, setPersistenceReady] = useState(false);

	useEffect(() => {
		const nextInitializationKey = designId
			? `design:${designId}`
			: shareConfig
				? `share:${JSON.stringify(shareConfig).slice(0, 120)}`
				: `seed:${initialSeedKey}`;
		if (lastInitializationKeyRef.current === nextInitializationKey) {
			// Re-runs while loadDesign is still in flight (StrictMode, the ?view=
			// normalization, the type-canonicalization redirect) must not arm
			// autosave early — the route's type is not reconciled until the load
			// resolves, and autosave writes that type back to the saved row.
			if (!pendingDesignLoadRef.current) {
				setPersistenceReady(true);
			}
			return;
		}

		// Set the key eagerly so re-renders from unrelated dep changes
		// (e.g. searchParams normalization) don't re-trigger the effect.
		lastInitializationKeyRef.current = nextInitializationKey;
		setPersistenceReady(false);

		if (designId) {
			pendingDesignLoadRef.current = true;
			loadDesign(designId).then((design) => {
				pendingDesignLoadRef.current = false;
				if (!design) {
					// Design not found or unauthorized — reset to safe seed defaults
					resetToDefaults({
						type,
						dimensions: initialDimensions,
						partitionSplit: initialPartitionSplit,
						twoRoomConfig: initialTwoRoomConfig,
						market,
						thermalInputs: initialThermalInputs,
						siteConditions: initialSiteConditions,
						roofConfig: initialRoofConfig,
						locationMode: market === "intl" ? "manual_climate" : "postal_code",
						postalCode: market === "se" ? wizardZip : "",
						wizardCapacityHint: initialWizardCapacityHint,
						climateProfile:
							market === "intl" || wizardClimate
								? initialClimateProfile
								: undefined,
					});
					// Strip the invalid designId from the URL to avoid retry loops
					mergeSearchParamsRef.current({ id: "" }, { replace: true });
					// Patch the key to seed so the builder can re-init from scratch
					lastInitializationKeyRef.current = `seed:${initialSeedKey}`;
					// This is now an anonymous seed draft — restore device-local progress.
					restoreLocalBuildProgress(type, initialSeedKey);
				} else if (design.type !== type) {
					// The URL's :type disagrees with the saved row (stale/edited link, or
					// the unknown-type fallback). Canonicalize the route to the design's
					// actual type WITHOUT setting persistenceReady — autosave must not
					// start while getType() would write the wrong type over the row.
					// The effect re-run after navigation hits the same-init-key early
					// return, which flips persistenceReady once the types agree.
					navigateToTypeRef.current(design.type);
					return;
				}

				setPersistenceReady(true);
			});
		} else if (shareConfig) {
			// Mark unsettled in ITS OWN commit before the hydration below. The
			// hydration itself is synchronous (no network round-trip — the data
			// is already in the URL), so without this the milestone hook's very
			// first post-mount observation would already be the fully-hydrated
			// design, which a mounted rules/completeness milestone reads as a
			// fresh "pending -> reached" transition and wrongly celebrates. The
			// `loadDesign` path gets this middle "unknown" render for free from
			// its network await; `queueMicrotask` gives the same shape here
			// without a real request.
			useBuilderStore.setState({ loadingDesign: true });
			queueMicrotask(() => {
				// Lossless hydration from a shared config — all fields preserved
				const resolvedMarket = shareConfig.market ?? market;
				const resolvedType = shareConfig.type ?? type;
				useBuilderStore.setState({
					...configToStoreState(shareConfig, resolvedMarket, resolvedType, initialClimateProfile),
					loadingDesign: false,
				});
				// Strip the share param after preserving route-owned two-room config fields.
				mergeSearchParamsRef.current(
					{
						share: "",
						...(shareConfig.entryWall
							? { entryWall: shareConfig.entryWall }
							: {}),
						...(shareConfig.glazingStyle
							? { glazingStyle: shareConfig.glazingStyle }
							: {}),
						...(shareConfig.saunaPosition
							? { saunaPosition: shareConfig.saunaPosition }
							: {}),
						...(shareConfig.hasRelaxRoom !== undefined
							? { hasRelaxRoom: String(shareConfig.hasRelaxRoom) }
							: {}),
					},
					{ replace: true },
				);
				// Prevent the next render (caused by the URL change above) from triggering a reset
				lastInitializationKeyRef.current = `seed:${initialSeedKey}`;
				setPersistenceReady(true);
			});
		} else {
			// An unsaved draft of this same seed, kept on this device: reopen it
			// instead of the defaults, through the same hydration a share link
			// uses (and the same unsettled-first render, so milestones do not
			// celebrate a restored design as freshly completed).
			const localDraft = readLocalDraft(draftLocalKey(type, initialSeedKey));
			if (localDraft && (localDraft.type ?? type) === type) {
				useBuilderStore.setState({ loadingDesign: true });
				queueMicrotask(() => {
					useBuilderStore.setState({
						...configToStoreState(localDraft, market, type, initialClimateProfile),
						buildProgress: {},
						loadingDesign: false,
					});
					restoreLocalBuildProgress(type, initialSeedKey);
					setPersistenceReady(true);
				});
				return;
			}
			resetToDefaults({
				type,
				dimensions: initialDimensions,
				partitionSplit: initialPartitionSplit,
				twoRoomConfig: initialTwoRoomConfig,
				market,
				thermalInputs: initialThermalInputs,
				siteConditions: initialSiteConditions,
				roofConfig: initialRoofConfig,
				locationMode: market === "intl" ? "manual_climate" : "postal_code",
				postalCode: market === "se" ? wizardZip : "",
				wizardCapacityHint: initialWizardCapacityHint,
				climateProfile:
					market === "intl" || wizardClimate
						? initialClimateProfile
						: undefined,
			});
			// Anonymous / unsaved draft — restore any device-local build progress.
			restoreLocalBuildProgress(type, initialSeedKey);
			setPersistenceReady(true);
		}
	}, [
		designId,
		initialClimateProfile,
		initialDimensions,
		initialPartitionSplit,
		initialRoofConfig,
		initialSeedKey,
		initialSiteConditions,
		initialThermalInputs,
		initialTwoRoomConfig,
		initialWizardCapacityHint,
		market,
		resetToDefaults,
		shareConfig,
		type,
		wizardClimate,
		wizardZip,
	]);

	useEffect(() => {
		if (!persistenceReady) {
			return;
		}

		const stop = startAutoSave({
			getUserId: () => userId,
			getDesignId: () => designId,
			getType: () => type,
			setSearchParams: mergeSearchParams,
		});

		return stop;
	}, [designId, mergeSearchParams, persistenceReady, type, userId]);

	// Device-local persistence for anonymous / unsaved drafts. Supabase (via
	// startAutoSave) owns progress once a designId exists, so this only runs while
	// the draft has no id. buildProgress is excluded from zundo's partialize, so
	// persisting it here does not touch undo/redo history.
	useEffect(() => {
		if (!persistenceReady || designId) {
			return;
		}

		const localKey = buildProgressLocalKey(type, initialSeedKey);
		// Persist the current value once (covers the case where progress was toggled
		// before this subscription attached) and on every subsequent change.
		writeLocalBuildProgress(localKey, useBuilderStore.getState().buildProgress);

		return useBuilderStore.subscribe((state, prevState) => {
			if (state.buildProgress === prevState.buildProgress) return;
			writeLocalBuildProgress(localKey, state.buildProgress);
		});
	}, [designId, initialSeedKey, persistenceReady, type]);

	// Device-local draft for the same unsaved designs: every change is kept on
	// this device (shortly after it settles, and at once when the page hides),
	// so a reload reopens the design instead of the defaults.
	useEffect(() => {
		if (!persistenceReady || designId) {
			return;
		}

		const key = draftLocalKey(type, initialSeedKey);
		let pending: ReturnType<typeof setTimeout> | undefined;
		const write = () => {
			pending = undefined;
			writeLocalDraft(key, buildShareableConfig(useBuilderStore.getState(), type));
		};
		const flush = () => {
			if (pending !== undefined) {
				clearTimeout(pending);
				write();
			}
		};
		const unsubscribe = useBuilderStore.subscribe((state, prevState) => {
			if (state === prevState) return;
			if (pending !== undefined) clearTimeout(pending);
			pending = setTimeout(write, DRAFT_WRITE_DELAY_MS);
		});
		window.addEventListener("pagehide", flush);
		return () => {
			window.removeEventListener("pagehide", flush);
			flush();
			unsubscribe();
		};
	}, [designId, initialSeedKey, persistenceReady, type]);

	const saveNow = useCallback(async () => {
		const localKey = buildProgressLocalKey(type, initialSeedKey);
		// Fold device-local progress into the store before the save payload is built
		// so an anonymous draft's checked-off work follows it into the design row.
		// Union merge — server-side progress is never destructively overwritten.
		if (!designId) {
			const local = readLocalBuildProgress(localKey);
			if (Object.keys(local).length > 0) {
				const merged = mergeBuildProgress(
					useBuilderStore.getState().buildProgress,
					local,
				);
				useBuilderStore.setState({ buildProgress: merged });
			}
		}

		const hadDesignId = Boolean(designId);
		const saved = await storeRef.current.saveNow({
			userId,
			designId,
			type,
			setSearchParams: mergeSearchParams,
		});

		// A successful anonymous save creates a design row and writes its id into the
		// URL. The merged progress is now persisted server-side, so clear the
		// device-local key to avoid re-merging stale completions on a later reload.
		// A failed save leaves no id, so the local copy is intentionally kept.
		if (saved && !hadDesignId && userId) {
			const created = new URLSearchParams(window.location.search).get("id");
			if (created) {
				clearLocalBuildProgress(localKey);
				// The design now lives in its row; the device copy would otherwise
				// reopen as a second, diverging draft of the same seed.
				clearLocalDraft(draftLocalKey(type, initialSeedKey));
			}
		}
		return saved;
	}, [designId, initialSeedKey, mergeSearchParams, type, userId]);

	useEffect(() => {
		return useBuilderStore.subscribe((state) => {
			storeRef.current = state;
		});
	}, []);

	const temporalUndo = useTemporalStore((state) => state.undo);
	const temporalRedo = useTemporalStore((state) => state.redo);
	// Zundo's undo/redo take an optional `steps` number. They must never be
	// passed straight to onClick: React would supply the MouseEvent as `steps`,
	// making splice(-NaN, NaN) return [] and setState(undefined) wipe the whole
	// store (blank-page crash). Wrap them so callers can't leak arguments.
	const undo = useCallback(() => temporalUndo(), [temporalUndo]);
	const redo = useCallback(() => temporalRedo(), [temporalRedo]);
	const pastStates = useTemporalStore((state) => state.pastStates);
	const futureStates = useTemporalStore((state) => state.futureStates);
	const canUndo = pastStates.length > 0;
	const canRedo = futureStates.length > 0;

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const usesModifier = event.metaKey || event.ctrlKey;
			if (!usesModifier) {
				return;
			}

			const tagName = (event.target as HTMLElement | null)?.tagName;
			if (tagName === "INPUT" || tagName === "TEXTAREA") {
				return;
			}

			if (event.key === "z" && !event.shiftKey) {
				event.preventDefault();
				undo();
			} else if ((event.key === "z" && event.shiftKey) || event.key === "Z") {
				event.preventDefault();
				redo();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [redo, undo]);

	return {
		saveNow,
		undo,
		redo,
		canUndo,
		canRedo,
	};
}
